/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box, Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isNil } from 'lodash';
import appTheme from '../../common/theme/appTheme.json';
export interface ManualDowntimeDateInputsProps {
  startDateChangeHandler: (startDate: any) => void;
  endDateChangeHandler: (endDate: any) => void;
  manualDowntimePeriodsRerenderInputsKey: number;
}

export const DATE_PICKER_MIN_DATE = dayjs('2010-01-01');

const ManualDowntimeDateInputs = ({
  startDateChangeHandler,
  endDateChangeHandler,
  manualDowntimePeriodsRerenderInputsKey,
}: ManualDowntimeDateInputsProps) => {
  dayjs.extend(utc);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} paddingY={2} sx={{}}>
        <Box>
          <Box sx={{ color: '#9FAFBC', fontSize: '14px', lineHeight: '20px', height: '28px', fontWeight: 600 }}>
            Downtime start
          </Box>
          <DesktopDateTimePicker
            ampm={false}
            sx={{ width: '236px' }}
            key={manualDowntimePeriodsRerenderInputsKey}
            minDate={DATE_PICKER_MIN_DATE}
            views={['day', 'month', 'year', 'hours', 'minutes', 'seconds']}
            format="DD MMM YYYY HH:mm[z]"
            onChange={newValue => {
              if (!isNil(newValue)) {
                startDateChangeHandler(dayjs(newValue.utc()));
              } else {
                startDateChangeHandler(null);
              }
            }}
            slotProps={datePickerSlotProps}
          />
        </Box>
        <Box>
          <Box sx={{ color: '#9FAFBC', fontSize: '14px', lineHeight: '20px', height: '28px', fontWeight: 600 }}>
            Downtime end
          </Box>
          <DesktopDateTimePicker
            ampm={false}
            key={manualDowntimePeriodsRerenderInputsKey}
            sx={{ width: '236px' }}
            minDate={DATE_PICKER_MIN_DATE}
            views={['day', 'month', 'year', 'hours', 'minutes', 'seconds']}
            format="DD MMM YYYY HH:mm[z]"
            onChange={newValue => {
              if (!isNil(newValue)) {
                endDateChangeHandler(newValue.utc());
              } else {
                endDateChangeHandler(null);
              }
            }}
            slotProps={datePickerSlotProps}
          />
        </Box>
      </Stack>
    </LocalizationProvider>
  );
};
export default ManualDowntimeDateInputs;

const datePickerSlotProps = {
  textField: {
    sx: {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#9FAFBC',
        },
        '&:hover fieldset': {
          borderColor: 'white',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00C2DE',
        },
      },
      input: { color: appTheme.primaryTextColor, height: '8px', width: '236px', textTransform: 'uppercase' },
      fieldset: {
        borderColor: appTheme.primaryTextColor,
      },
    },
  },

  popper: {
    sx: {
      '.MuiDayCalendar-weekDayLabel': {
        color: appTheme.secondaryTextColor,
      },
      '.MuiPickersDay-root': {
        color: appTheme.primaryTextColor,
        backgroundColor: appTheme.primaryBackgroundColor,
        '&:hover': {
          backgroundColor: `${appTheme.mptSelectIconGray}`,
          color: appTheme.primaryTextColor,
        },
      },
      '.Mui-selected': {
        backgroundColor: `${appTheme.mptLightBlue} !important`,
        color: appTheme.primaryBackgroundColor,
        '&:hover': {
          backgroundColor: `${appTheme.mptSelectIconGray} !important`,
          color: `${appTheme.primaryTextColor} !important`,
        },
      },
    },
  },
};
